/**
 * CampaignRankingSheetContent
 *
 * Body of the campaign ranking sheet (rendered by SheetService). Drives the
 * `useCampaignRanking` hook on mount and renders one of three states:
 * loading → empty ("No data") → a `VirtualList` of ranking rows.
 *
 * Mirrors Flutter's `CampaignRankingBottomSheet.build`
 * (lib/presentation/bottom_sheet/campaign_ranking_bottom_sheet.dart):
 * - `:238-260` — loading: centered `ExpauseLoadingIndicator`.
 * - `:262-270` — empty: centered "No data".
 * - `:271-363` — list: `usersMostSubscribers` iterates the user list and renders
 *   `CampainRankingUserItem`; otherwise iterates the content list and renders
 *   `CampainRankingContentItem`.
 * - `:79-94` — `_itemTapped` → user details; `_onItemMediaTap` → the content.
 *
 * The web replaces Flutter's `ListView.builder` with `VirtualList` (convention)
 * and the bespoke drag-to-dismiss surface with the standard `SheetService`
 * surface (a permitted UI deviation; behaviour is equivalent).
 *
 * Task 10 supplies the real row components (`CampaignRankingUserItem` /
 * `CampaignRankingContentItem`). Until then a minimal placeholder row renders,
 * carrying the row `data-qa-id`s the UI-test plan references so QA can locate
 * them; Task 10 swaps the placeholder body for the real components.
 */
import React, { useEffect, useCallback } from 'react';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { useLocale } from '@/presentation/contexts/LocaleContext';
import { Navigator } from '@/presentation/routing/Navigator';
import { ExpauseLoadingIndicator } from '@/presentation/components/common/loadingIndicators/ExpauseLoadingIndicator';
import { VirtualList } from '@/presentation/components/common/virtualList/VirtualList';
import { qaAttr } from '@/presentation/utils/qaAttr';
import { CampaignType } from '@/domain/enums/campaign/campaignType';
import type { CampaignEntity } from '@/domain/entities/campaign/campaignEntity';
import type { CampaignRankingUserEntity } from '@/domain/entities/campaign/campaignRankingUserEntity';
import type { CampaignRankingContentEntity } from '@/domain/entities/campaign/campaignRankingContentEntity';
import { useCampaignRanking } from '../hooks/useCampaignRanking';
import { CampaignRankingUserItem } from './CampaignRankingUserItem';
import { CampaignRankingContentItem } from './CampaignRankingContentItem';

interface CampaignRankingSheetContentProps {
  campaign: CampaignEntity;
}

export const CampaignRankingSheetContent: React.FC<CampaignRankingSheetContentProps> = ({
  campaign,
}) => {
  const { theme } = useTheme();
  const responsive = useResponsiveDesign();
  const { l10n } = useLocale();

  const {
    campaignType,
    users,
    content,
    isFetching,
    isEmpty,
    fetchRanking,
    isCurrentUser,
  } = useCampaignRanking(campaign);

  // Mirrors Flutter `initState` kicking off `_getData()` once.
  useEffect(() => {
    void fetchRanking();
    // Run once on mount; `fetchRanking` is stable per campaign.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Mirrors Flutter `_itemTapped` (:80): tap a ranking row → user details.
  const handleUserTap = useCallback((firebaseId: string) => {
    Navigator.navigateToUserDetails(firebaseId);
  }, []);

  // Mirrors Flutter `_onItemMediaTap` (:83-94): tap a content row → that
  // content. Flutter opens the content list seeded with the single media via
  // `fetchSingleMediaPath`; the closest web analog is the by-path content
  // details route. See `**Deviations from plan:**` in task_9_plan.md.
  const handleContentTap = useCallback((item: CampaignRankingContentEntity) => {
    Navigator.navigateToContentDetailsByPath(item.relativePath);
  }, []);

  // Coarse lifecycle token so the qa-tester can `wait_for` the fetched state.
  const sheetStatus = isFetching ? 'loading' : isEmpty ? 'empty' : 'ready';

  const renderUserRow = useCallback(
    (item: CampaignRankingUserEntity) => (
      <CampaignRankingUserItem
        user={item}
        isCurrentUser={isCurrentUser(item.firebaseId)}
        onTap={handleUserTap}
      />
    ),
    [handleUserTap, isCurrentUser],
  );

  const renderContentRow = useCallback(
    (item: CampaignRankingContentEntity) => (
      <CampaignRankingContentItem
        content={item}
        isCurrentUser={isCurrentUser(item.firebaseId)}
        onItemTap={handleUserTap}
        onMediaTap={handleContentTap}
      />
    ),
    [handleContentTap, handleUserTap, isCurrentUser],
  );

  return (
    <div
      {...qaAttr({ id: 'campaign-ranking', status: sheetStatus })}
      style={{ display: 'flex', flexDirection: 'column' }}
    >
      {isFetching ? (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: responsive.spacing['3xl'],
          }}
        >
          <ExpauseLoadingIndicator />
        </div>
      ) : isEmpty ? (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: responsive.spacing['3xl'],
          }}
        >
          <span style={{ color: theme.text.secondary, fontSize: responsive.font.base }}>
            {l10n.common.noData}
          </span>
        </div>
      ) : campaignType === CampaignType.usersMostSubscribers ? (
        <VirtualList<CampaignRankingUserEntity>
          qa={{ id: 'campaign-ranking-list' }}
          items={users}
          getItemKey={(item) => item.firebaseId}
          renderItem={renderUserRow}
          gap={responsive.spacing.base}
          style={{ padding: responsive.spacing.base }}
        />
      ) : (
        <VirtualList<CampaignRankingContentEntity>
          qa={{ id: 'campaign-ranking-list' }}
          items={content}
          getItemKey={(item) => item.firebaseId}
          renderItem={renderContentRow}
          gap={responsive.spacing.base}
          style={{ padding: responsive.spacing.base }}
        />
      )}
    </div>
  );
};
